import * as Sentry from '@sentry/vue'
import { pwaConfig } from './pwaTools'
import { sentryCountMetric } from './sentryMetrics.ts'
import { handleSwAuthTelemetryMessage } from './swAuthTelemetry.ts'
import { AUTO_RELOAD_BUDGET_MAX, AUTO_RELOAD_BUDGET_WINDOW_MS } from './autoReloadBudget'

/** Page loads in this window suggest a reload loop (any cause: SW, store, chunk, IDB, etc.). */
export const RELOAD_LOOP_WINDOW_MS = AUTO_RELOAD_BUDGET_WINDOW_MS
export const RELOAD_LOOP_MIN_LOADS = AUTO_RELOAD_BUDGET_MAX

const LOAD_TIMESTAMPS_KEY = 'gh_page_load_timestamps_v1'
const LOOP_REPORTED_KEY = 'gh_reload_loop_reported_v1'
const LAST_RELOAD_INTENT_KEY = 'gh_last_reload_intent_v1'

export type ReloadIntent = {
  reason: string
  at: number
}

export function markImpendingReload(reason: string): void {
  try {
    const intent: ReloadIntent = { reason, at: Date.now() }
    sessionStorage.setItem(LAST_RELOAD_INTENT_KEY, JSON.stringify(intent))
  } catch {
    // sessionStorage may be unavailable
  }
}

function readLastReloadIntent(): ReloadIntent | null {
  try {
    const raw = sessionStorage.getItem(LAST_RELOAD_INTENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ReloadIntent
    if (typeof parsed?.reason === 'string' && typeof parsed?.at === 'number') {
      return parsed
    }
  } catch {
    // ignore
  }
  return null
}

function getRecentLoadTimestamps(now: number): number[] {
  try {
    const raw = sessionStorage.getItem(LOAD_TIMESTAMPS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (value): value is number =>
        typeof value === 'number' && Number.isFinite(value) && now - value <= RELOAD_LOOP_WINDOW_MS,
    )
  } catch {
    return []
  }
}

function persistLoadTimestamps(timestamps: number[]): void {
  try {
    sessionStorage.setItem(LOAD_TIMESTAMPS_KEY, JSON.stringify(timestamps))
  } catch {
    // ignore
  }
}

function getNavigationType(): string {
  if (typeof performance === 'undefined') return 'unknown'
  const entry = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined
  return entry?.type ?? 'unknown'
}

/**
 * Records this document load and reports a Sentry error once per tab session if the user
 * appears stuck in a rapid full reload loop (independent of root cause).
 */
export function recordPageLoadAndReportReloadLoopIfNeeded(): void {
  const now = Date.now()
  const loads = getRecentLoadTimestamps(now)
  loads.push(now)
  persistLoadTimestamps(loads)

  if (loads.length < RELOAD_LOOP_MIN_LOADS) return

  try {
    if (sessionStorage.getItem(LOOP_REPORTED_KEY) === '1') return
    sessionStorage.setItem(LOOP_REPORTED_KEY, '1')
  } catch {
    return
  }

  const lastIntent = readLastReloadIntent()
  const error = new Error(
    `Client reload loop detected: ${loads.length} full loads within ${RELOAD_LOOP_WINDOW_MS / 1000}s`,
  )

  Sentry.withScope((scope) => {
    scope.setLevel('error')
    scope.setFingerprint(['client-reload-loop-detected'])
    scope.setTag('client.issue', 'reload_loop')
    scope.setTag('navigation.type', getNavigationType())
    scope.setTag('displayMode', pwaConfig.displayMode)
    scope.setTag('browserInfoName', pwaConfig.browserInfo.name)
    scope.setTag('browserInfoOs', pwaConfig.browserInfo.os)
    if (lastIntent) {
      scope.setTag('reload.last_reason', lastIntent.reason)
    }
    scope.setExtras({
      loadTimestamps: loads,
      windowMs: RELOAD_LOOP_WINDOW_MS,
      minLoads: RELOAD_LOOP_MIN_LOADS,
      lastReloadIntent: lastIntent,
      pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
      swController: Boolean(
        typeof navigator !== 'undefined' && navigator.serviceWorker?.controller,
      ),
    })
    Sentry.captureException(error)
  })

  sentryCountMetric('client.reload_loop_detected')
}

/** Wire SW → page telemetry and run reload-loop detection at startup. */
export function initClientMonitoring(): void {
  recordPageLoadAndReportReloadLoopIfNeeded()

  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    handleSwAuthTelemetryMessage(event.data)
  })
}
